import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../Footer/Footer';
import { getCurrentUser } from '../../services/cognito';
import AssessmentWizard from '../AssessmentWizard';
import { MarketService, getAppMarket } from '../../services/MarketService';

const trackEvent = (eventName: string, params?: Record<string, string>) => {
    // Push to dataLayer for GTM
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...params });
    // Also send via gtag for direct GA4 capture
    if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
    }
};

const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const appMarket = getAppMarket();
    const marketInfo = MarketService.getMarketInfo(appMarket === 'hub' ? 'uk' : appMarket);

    // Redirect logged-in users to dashboard
    useEffect(() => {
        const user = getCurrentUser();
        if (user) {
            navigate('/dashboard');
        }
    }, [navigate]);

    return (
        <div className="bg-gray-50 font-sans text-gray-900">
            {/* NAV */}
            <nav className="bg-white shadow sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 flex items-center justify-center rounded bg-emerald-600">
                            <i className="fas fa-home text-white text-sm"></i>
                        </div>
                        <span className="font-bold text-lg">Landlord Pro</span>
                        <span className="ml-1 bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full text-xs font-semibold">
                            {marketInfo.flagEmoji} {marketInfo.code} Market
                        </span>
                    </div>

                    <div className="flex items-center gap-6">
                        <button
                            onClick={() => navigate('/features')}
                            className="text-gray-600 hover:text-emerald-600 font-medium transition-colors"
                        >
                            Features
                        </button>
                        <a
                            href="https://blog.compliance-engine.io/?s=Landlord"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-600 hover:text-emerald-600 font-medium transition-colors"
                        >
                            Blog
                        </a>
                        <button
                            onClick={() => { trackEvent('cta_click', { button_name: 'login', location: 'nav' }); navigate('/login'); }}
                            className="text-gray-600 hover:text-emerald-600 font-medium transition-colors"
                        >
                            Login
                        </button>
                        <button
                            onClick={() => { trackEvent('cta_click', { button_name: 'start_free_trial', location: 'nav' }); navigate('/signup'); }}
                            className="bg-emerald-600 text-white px-5 py-2 rounded font-semibold hover:bg-emerald-700 transition"
                        >
                            Start Free Trial
                        </button>
                    </div>
                </div>
            </nav>

            {/* HERO */}
            <section className="bg-gradient-to-br from-green-600 to-emerald-800 text-white py-20">
                <div className="max-w-screen-2xl mx-auto px-6">

                    {/* Headline + sub-headline */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 max-w-5xl mx-auto leading-tight">
                            If this ends up in court, can you prove you did everything right?
                        </h1>
                        <p className="text-lg md:text-xl text-green-50 max-w-4xl mx-auto leading-relaxed">
                            Landlord Pro is the legal shield for UK letting agents, landlords, housing associations,
                            and corporate portfolios — built on AWS-elastic infrastructure that scales from 20 to
                            50,000 properties on the same code.
                        </p>
                    </div>

                    {/* Who We Serve — 4 audience columns spanning the full green area */}
                    <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 mb-12">

                        {/* New Landlord */}
                        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 flex flex-col hover:bg-white/20 transition">
                            <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-orange-900/30">
                                <i className="fas fa-seedling text-white text-2xl" aria-hidden="true"></i>
                            </div>
                            <h3 className="text-lg font-bold mb-3 leading-snug">New Landlord</h3>
                            <p className="text-sm text-green-50 leading-relaxed flex-grow">
                                1 Property — <span className="font-bold text-yellow-300">FREE</span> forever.
                                Get protected from day one with the Evidence Vault and PRS readiness scoring.
                            </p>
                        </div>

                        {/* Independent Landlords, Estate & Letting Agents */}
                        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 flex flex-col hover:bg-white/20 transition">
                            <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-emerald-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-emerald-900/30">
                                <i className="fas fa-handshake text-white text-2xl" aria-hidden="true"></i>
                            </div>
                            <h3 className="text-lg font-bold mb-3 leading-snug">
                                Independent Landlords, Estate &amp; Letting Agents
                            </h3>
                            <p className="text-sm text-green-50 leading-relaxed flex-grow">
                                2 to 100 properties. Court-grade Evidence Vault, AI Legal Q&amp;A,
                                Renters' Rights Act 2025 notice generation, and irrefutable audit trails.
                            </p>
                        </div>

                        {/* Housing Associations / Registered Providers / Social Landlords */}
                        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 flex flex-col hover:bg-white/20 transition">
                            <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-purple-600 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-purple-900/30">
                                <i className="fas fa-users text-white text-2xl" aria-hidden="true"></i>
                            </div>
                            <h3 className="text-lg font-bold mb-3 leading-snug">
                                Housing Associations, Registered Providers or Social Landlords
                            </h3>
                            <p className="text-sm text-green-50 leading-relaxed flex-grow">
                                Awaab's Law ready, Tenant Satisfaction Measures (TSM) evidence packs,
                                AI Legal Q&amp;A trained on social housing law.
                            </p>
                        </div>

                        {/* Corporate Portfolio Operators / REITs */}
                        <div className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 flex flex-col hover:bg-white/20 transition">
                            <div className="w-14 h-14 bg-gradient-to-br from-sky-400 to-indigo-700 rounded-xl flex items-center justify-center mb-4 shadow-lg shadow-indigo-900/30">
                                <i className="fas fa-city text-white text-2xl" aria-hidden="true"></i>
                            </div>
                            <h3 className="text-lg font-bold mb-3 leading-snug">
                                Corporate Portfolio Operators, REITs, Portfolio Owners
                            </h3>
                            <p className="text-sm text-green-50 leading-relaxed flex-grow">
                                Versioned API, white-label branding, OWASP-hardened security,
                                elastic AWS-serverless infrastructure to 50,000+ units.
                            </p>
                        </div>

                    </div>

                    {/* Single CTA */}
                    <div className="text-center">
                        <a
                            href="https://calendar.app.google/E2NdqWZSh2csxTAC7"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block bg-yellow-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-500 transition shadow-lg"
                        >
                            Book a 15-minute platform demo →
                        </a>
                    </div>

                </div>
            </section>

            {/* PRICING */}
            <section className="py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">

                    {/* NEEDS ASSESSMENT — FIRST */}
                    <div className="mb-16">
                        <div className="max-w-3xl mx-auto">
                            <h3 className="text-3xl font-bold text-center mb-2">Find Your Perfect Plan</h3>
                            <p className="text-center text-gray-500 mb-8">Answer 3 quick questions and we'll recommend the right plan for you.</p>
                            <AssessmentWizard />
                        </div>
                    </div>

                    {/* ONE-OFF PRICING SERVICES — SECOND */}
                    <div>
                        <h3 className="text-3xl font-bold text-center mb-2">One-off Pricing Services</h3>
                        <p className="text-center text-gray-500 mb-8 max-w-2xl mx-auto">
                            No subscription needed — buy only what you need. <span className="font-semibold">Sign-up required</span> for access and delivery.
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {[
                                { name: 'Order Certificate', price: '£19 one-off', icon: 'fa-certificate', desc: 'Gas Safety, EICR, EPC & more' },
                                { name: 'Tenancy Agreement Validator', price: '£29 one-off', icon: 'fa-file-signature' },
                                { name: 'Eviction Notice Pack', price: '£49 one-off', icon: 'fa-gavel' },
                                { name: 'Tenant Onboarding', price: '£19 one-off', icon: 'fa-user-plus' },
                                { name: 'Generate Possession Notice', price: '£29 one-off', icon: 'fa-file-circle-exclamation' },
                            ].map((svc, i) => (
                                <div key={i} className="bg-white rounded-2xl shadow p-5 text-center hover:shadow-md transition">
                                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                        <i className={`fas ${svc.icon} text-emerald-600 text-xl`}></i>
                                    </div>
                                    <span className="font-semibold text-gray-900 block mb-1 text-sm">{svc.name}</span>
                                    {svc.desc && <span className="text-xs text-gray-500 block mb-2">{svc.desc}</span>}
                                    <span className="text-xl font-bold text-emerald-600">{svc.price}</span>
                                    <button
                                        onClick={() => { trackEvent('cta_click', { button_name: 'oneoff_buy', service: svc.name, location: 'homepage' }); navigate('/signup?service=' + encodeURIComponent(svc.name)); }}
                                        className="mt-3 w-full bg-emerald-600 text-white py-2 rounded-lg font-semibold text-sm hover:bg-emerald-700 transition"
                                    >
                                        Buy now
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </section>

            {/* FOOTER */}
            <Footer />

        </div>
    );
};

export default LandingPage;
